export type EntityType = 'vehicle' | 'driver' | 'company' | 'helper' | 'staff' | 'client';

export type VerificationStatus = 'pending' | 'approved' | 'rejected';
export type ExpiryStatus =
  | 'valid'
  | 'active'
  | 'expiring_7'
  | 'expiring_30'
  | 'expiring_soon'
  | 'expired'
  | 'no_expiry'
  | 'missing';

export interface DocumentRecord {
  _id: string;
  tenantId: string;
  entityType: EntityType;
  entityId: string;
  documentType: string;
  documentNumber?: string;
  issueDate?: string;
  expiryDate?: string;
  fileName?: string;
  fileUrl?: string;
  filePath?: string;
  mimeType?: string;
  sizeBytes?: number;
  verificationStatus: VerificationStatus;
  expiryStatus: ExpiryStatus;
  daysUntilExpiry?: number | null;
  expiryLabel?: string;
  remarks?: string;
  uploadedBy?: { _id: string; name?: string; email?: string };
  createdAt?: string;
  updatedAt?: string;
}

export interface VehicleDocAlert {
  documentType: string;
  label: string;
  status: ExpiryStatus;
  documentNumber?: string;
  issueDate?: string | null;
  expiryDate?: string | null;
  expiryLabel?: string;
  daysUntilExpiry?: number | null;
  documentId?: string | null;
  hasFile?: boolean;
}

export interface VehicleDocSlot {
  documentType: string;
  label: string;
  required: boolean;
  status: ExpiryStatus;
  document?: DocumentRecord | null;
  documents?: DocumentRecord[];
}

export interface VehicleComplianceRow {
  vehicleId: string;
  vehicleNumber: string;
  vehicleType?: string;
  vehicleStatus?: string;
  overallStatus: ExpiryStatus;
  slots: VehicleDocSlot[];
  alerts: VehicleDocAlert[];
}

export interface VehicleComplianceSummary {
  expired: number;
  expiring_7: number;
  expiring_30: number;
  valid: number;
  missing: number;
  no_expiry: number;
  vehiclesTracked: number;
  vehiclesWithAlerts: number;
  vehiclesMissingDocs: number;
}

export const VEHICLE_DOCUMENT_TYPES = [
  { value: 'rc', label: 'RC', required: true },
  { value: 'fitness', label: 'Fitness', required: true },
  { value: 'insurance', label: 'Insurance', required: true },
  { value: 'puc', label: 'PUC', required: true },
  { value: 'permit', label: 'Permit', required: true },
  { value: 'national_permit', label: 'National Permit', required: true },
  { value: 'state_permit', label: 'State Permit', required: true },
  { value: 'road_tax', label: 'Road Tax', required: true },
  { value: 'fastag', label: 'FASTag', required: true },
  { value: 'supportive', label: 'Supportive Document', required: false },
];

export const DRIVER_DOCUMENT_TYPES = [
  { value: 'driving_license', label: 'Driving License (DL)' },
  { value: 'aadhaar_front', label: 'Aadhaar Front' },
  { value: 'aadhaar_back', label: 'Aadhaar Back' },
  { value: 'photo', label: 'Photo' },
];

export const HELPER_DOCUMENT_TYPES = [
  { value: 'driving_license', label: 'Driving License (DL)' },
  { value: 'aadhaar_front', label: 'Aadhaar Front' },
  { value: 'aadhaar_back', label: 'Aadhaar Back' },
  { value: 'photo', label: 'Photo' },
];

export const STAFF_DOCUMENT_TYPES = [
  { value: 'id_proof', label: 'ID Proof' },
  { value: 'offer_letter', label: 'Offer Letter' },
  { value: 'contract', label: 'Contract' },
  { value: 'background_check', label: 'Background Check' },
];

export const CLIENT_DOCUMENT_TYPES = [
  { value: 'agreement', label: 'Service Agreement' },
  { value: 'gst', label: 'GST Certificate' },
  { value: 'pan', label: 'PAN Card' },
  { value: 'contract', label: 'Contract' },
];

export const COMPANY_DOCUMENT_TYPES = [
  { value: 'gst', label: 'GST Certificate' },
  { value: 'pan', label: 'PAN Card' },
  { value: 'business_license', label: 'Business License' },
];

export const DOCUMENT_TYPES_BY_ENTITY: Record<EntityType, { value: string; label: string; required?: boolean }[]> = {
  vehicle: VEHICLE_DOCUMENT_TYPES,
  driver: DRIVER_DOCUMENT_TYPES,
  company: COMPANY_DOCUMENT_TYPES,
  helper: HELPER_DOCUMENT_TYPES,
  staff: STAFF_DOCUMENT_TYPES,
  client: CLIENT_DOCUMENT_TYPES,
};

export const ENTITY_TYPE_OPTIONS = [
  { value: 'vehicle', label: 'Vehicle' },
  { value: 'driver', label: 'Driver' },
  { value: 'helper', label: 'Helper' },
  { value: 'staff', label: 'Staff' },
  { value: 'client', label: 'Client' },
  { value: 'company', label: 'Company' },
] as const;

export const VERIFICATION_STATUS_OPTIONS = [
  { value: 'pending', label: 'Pending' },
  { value: 'approved', label: 'Approved' },
  { value: 'rejected', label: 'Rejected' },
];

export const EXPIRY_STATUS_OPTIONS = [
  { value: 'expired', label: 'Expired' },
  { value: 'expiring_7', label: 'Expiring within 7 days' },
  { value: 'expiring_30', label: 'Expiring within 30 days' },
  { value: 'valid', label: 'Valid' },
  { value: 'missing', label: 'Not uploaded' },
  { value: 'no_expiry', label: 'No expiry' },
];

export type ExpiryStatusMeta = {
  emoji: string;
  label: string;
  bg: string;
  color: string;
  border: string;
};

export function normalizeExpiryStatus(status?: string | null): ExpiryStatus {
  if (status === 'active') return 'valid';
  if (status === 'expiring_soon') return 'expiring_30';
  if (
    status === 'expired'
    || status === 'expiring_7'
    || status === 'expiring_30'
    || status === 'valid'
    || status === 'missing'
    || status === 'no_expiry'
  ) {
    return status;
  }
  return 'no_expiry';
}

export function getExpiryStatusMeta(status?: string | null): ExpiryStatusMeta {
  const key = normalizeExpiryStatus(status);
  const map: Record<ExpiryStatus, ExpiryStatusMeta> = {
    expired: { emoji: '🔴', label: 'Expired', bg: '#fee2e2', color: '#dc2626', border: '#fecaca' },
    expiring_7: { emoji: '🟠', label: 'Expiring within 7 days', bg: '#ffedd5', color: '#ea580c', border: '#fed7aa' },
    expiring_30: { emoji: '🟡', label: 'Expiring within 30 days', bg: '#fef3c7', color: '#d97706', border: '#fde68a' },
    valid: { emoji: '🟢', label: 'Valid', bg: '#dcfce7', color: '#16a34a', border: '#bbf7d0' },
    active: { emoji: '🟢', label: 'Valid', bg: '#dcfce7', color: '#16a34a', border: '#bbf7d0' },
    expiring_soon: { emoji: '🟡', label: 'Expiring within 30 days', bg: '#fef3c7', color: '#d97706', border: '#fde68a' },
    missing: { emoji: '⚪', label: 'Not uploaded', bg: '#f1f5f9', color: '#64748b', border: '#e2e8f0' },
    no_expiry: { emoji: '🟢', label: 'No expiry', bg: '#f1f5f9', color: '#64748b', border: '#e2e8f0' },
  };
  return map[key] || map.no_expiry;
}

export function getDocumentTypeLabel(entityType: EntityType, value?: string): string {
  if (!value) return '—';
  const match = (DOCUMENT_TYPES_BY_ENTITY[entityType] || []).find((t) => t.value === value);
  if (match) return match.label;
  // Legacy driver types still shown if already uploaded
  if (entityType === 'driver') {
    const legacy: Record<string, string> = {
      aadhaar: 'Aadhaar / ID Proof',
      medical: 'Medical Certificate',
      background_verification: 'Background Verification',
      photo: 'Photo',
    };
    if (legacy[value]) return legacy[value];
  }
  if (entityType === 'helper') {
    const legacy: Record<string, string> = {
      id_proof: 'ID Proof',
      contract: 'Contract / Agreement',
      medical: 'Medical Certificate',
      safety_training: 'Safety Training',
    };
    if (legacy[value]) return legacy[value];
  }
  return value.replace(/_/g, ' ');
}

export function getExpiryStatusColor(status: ExpiryStatus | string): { bg: string; color: string } {
  const meta = getExpiryStatusMeta(status);
  return { bg: meta.bg, color: meta.color };
}

export function getVerificationStatusColor(status: VerificationStatus): { bg: string; color: string } {
  const map: Record<VerificationStatus, { bg: string; color: string }> = {
    pending: { bg: '#fef3c7', color: '#d97706' },
    approved: { bg: '#dcfce7', color: '#16a34a' },
    rejected: { bg: '#fee2e2', color: '#dc2626' },
  };
  return map[status] || map.pending;
}

export function formatDocExpiry(date?: string | Date | null): string {
  if (!date) return '—';
  const d = new Date(date);
  if (Number.isNaN(d.getTime())) return '—';
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const pad = (n: number) => String(n).padStart(2, '0');
  const hasTime = d.getHours() !== 0 || d.getMinutes() !== 0;
  const day = `${pad(d.getDate())}-${months[d.getMonth()]}-${d.getFullYear()}`;
  return hasTime ? `${day} ${pad(d.getHours())}:${pad(d.getMinutes())}` : day;
}

export function formatFileSize(bytes?: number): string {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
